import api from '../lib/api';

export type Role = string;
export type PermissionType = 'view' | 'create' | 'edit' | 'delete';

/** Access column values: schedules use all/own/own_and_reports; customers use no_contact/full_contact. */
export type AccessScope =
  | 'all'
  | 'own'
  | 'own_and_reports'
  | 'no_contact'
  | 'full_contact';

export type Module =
  | 'shops'
  | 'staff'
  | 'customers'
  | 'products'
  | 'orders'
  | 'payments'
  | 'suppliers'
  | 'tags'
  | 'subscriptions'
  | 'journal'
  | 'payment_methods'
  | 'message_templates'
  | 'inventory'
  | 'bookings'
  | 'staff_time_entries'
  | 'schedules'
  | 'data_management'
  | 'messaging'
  | 'support_chat'
  | 'website'
  | 'reports'
  | 'class_packages'
  | 'payroll'
  | 'leave'
  | 'leave_policy'
  | 'events'
  | 'coupons'
  | 'social_promote'
  | 'teams'
  | 'automation'
  | 'companion_bundles'
  | 'audit';

export type RoleModuleScopes = Partial<Record<string, Partial<Record<Module, AccessScope>>>>;

export interface ModulePermissions {
  view: boolean;
  create: boolean;
  edit: boolean;
  delete: boolean;
}

export interface RolePermissions {
  [module: string]: ModulePermissions;
}

export interface AllRolePermissions {
  [role: string]: RolePermissions;
}

export type ShopRole = {
  shop_role_id: number;
  shop_id: number;
  role_key: string;
  display_name: string;
  is_system: boolean;
  sort_order: number;
};

export interface BulkUpdateRequest {
  role: string;
  shop_id: number;
  permissions: {
    module: Module;
    permission_type: PermissionType;
    is_allowed: boolean;
  }[];
  module_scopes?: {
    module: Module;
    access_scope: AccessScope;
  }[];
}

export interface StaffPermissionOverride {
  module: Module;
  permission_type: PermissionType;
  is_allowed: boolean;
}

export interface StaffModuleScopeOverride {
  module: Module;
  access_scope: AccessScope;
}

export interface StaffPermissionOverridesResponse {
  staff_id: number;
  shop_id: number;
  role: string;
  is_admin_role: boolean;
  role_permissions: RolePermissions | null;
  overrides: StaffPermissionOverride[];
  effective_permissions: RolePermissions | null;
  role_module_scopes?: Partial<Record<Module, AccessScope>> | null;
  module_scope_overrides?: StaffModuleScopeOverride[];
  effective_module_scopes?: Partial<Record<Module, AccessScope>> | null;
}

class RolePermissionsService {
  async getAllPermissions(shopId: number): Promise<{
    permissions: AllRolePermissions;
    module_scopes: RoleModuleScopes;
    roles: ShopRole[];
  }> {
    const response = await api.get<{
      success: boolean;
      data: {
        shop_id: number;
        permissions: AllRolePermissions;
        module_scopes?: RoleModuleScopes;
        roles?: ShopRole[];
      };
    }>(`/role-permissions?shop_id=${shopId}`);
    return {
      permissions: response.data.data.permissions,
      module_scopes: response.data.data.module_scopes ?? {},
      roles: response.data.data.roles ?? [],
    };
  }

  async listShopRoles(shopId: number): Promise<ShopRole[]> {
    const response = await api.get<{
      success: boolean;
      data: { shop_id: number; roles: ShopRole[] };
    }>(`/role-permissions/roles?shop_id=${shopId}`);
    return response.data.data.roles;
  }

  async createShopRole(shopId: number, displayName: string, cloneFrom = 'staff'): Promise<ShopRole> {
    const response = await api.post<{ success: boolean; data: ShopRole }>(`/role-permissions/roles`, {
      shop_id: shopId,
      display_name: displayName,
      clone_from: cloneFrom,
    });
    return response.data.data;
  }

  async renameShopRole(shopId: number, roleKey: string, displayName: string): Promise<ShopRole> {
    const response = await api.patch<{ success: boolean; data: ShopRole }>(
      `/role-permissions/roles/${encodeURIComponent(roleKey)}`,
      { shop_id: shopId, display_name: displayName },
    );
    return response.data.data;
  }

  async deleteShopRole(shopId: number, roleKey: string): Promise<void> {
    await api.delete(`/role-permissions/roles/${encodeURIComponent(roleKey)}?shop_id=${shopId}`);
  }

  async getRolePermissions(role: string, shopId: number): Promise<RolePermissions> {
    const response = await api.get<{
      success: boolean;
      data: { shop_id: number; role: string; permissions: RolePermissions };
    }>(`/role-permissions/role/${encodeURIComponent(role)}?shop_id=${shopId}`);
    return response.data.data.permissions;
  }

  async getModulePermissions(role: string, shopId: number, module: Module): Promise<ModulePermissions> {
    const response = await api.get<{
      success: boolean;
      data: { shop_id: number; role: string; module: string; permissions: ModulePermissions };
    }>(`/role-permissions/role/${encodeURIComponent(role)}/module/${module}?shop_id=${shopId}`);
    return response.data.data.permissions;
  }

  async updatePermission(
    role: string,
    shopId: number,
    module: Module,
    permissionType: PermissionType,
    isAllowed: boolean,
  ): Promise<void> {
    await api.put(
      `/role-permissions/role/${encodeURIComponent(role)}/module/${module}/permission/${permissionType}`,
      { shop_id: shopId, is_allowed: isAllowed },
    );
  }

  async bulkUpdatePermissions(request: BulkUpdateRequest): Promise<void> {
    await api.put('/role-permissions/bulk', request);
  }

  async getMyPermissions(): Promise<{
    role: string;
    permissions: RolePermissions;
    overrides?: StaffPermissionOverride[];
    module_scopes?: Partial<Record<Module, AccessScope>>;
  }> {
    const response = await api.get<{
      success: boolean;
      data: {
        role: string;
        permissions: RolePermissions;
        overrides?: StaffPermissionOverride[];
        module_scopes?: Partial<Record<Module, AccessScope>>;
      };
    }>('/role-permissions/me');
    return response.data.data;
  }

  async getStaffPermissionOverrides(staffId: number): Promise<StaffPermissionOverridesResponse> {
    const response = await api.get<{ success: boolean; data: StaffPermissionOverridesResponse }>(
      `/staff/${staffId}/permission-overrides`,
    );
    return response.data.data;
  }

  async updateStaffPermissionOverrides(
    staffId: number,
    overrides: StaffPermissionOverride[],
    moduleScopes?: StaffModuleScopeOverride[],
  ): Promise<{
    overrides: StaffPermissionOverride[];
    effective_permissions: RolePermissions;
    role_module_scopes?: Partial<Record<Module, AccessScope>>;
    module_scope_overrides?: StaffModuleScopeOverride[];
    effective_module_scopes?: Partial<Record<Module, AccessScope>>;
  }> {
    const response = await api.put<{
      success: boolean;
      data: {
        overrides: StaffPermissionOverride[];
        effective_permissions: RolePermissions;
        role_module_scopes?: Partial<Record<Module, AccessScope>>;
        module_scope_overrides?: StaffModuleScopeOverride[];
        effective_module_scopes?: Partial<Record<Module, AccessScope>>;
      };
    }>(`/staff/${staffId}/permission-overrides`, {
      overrides,
      module_scopes: moduleScopes ?? [],
    });
    return response.data.data;
  }
}

export const rolePermissionsService = new RolePermissionsService();
